import React, { useState, useEffect } from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useAuth } from "../firebase";
import AlertDialog from "./AlertDialog";
import { useAlertDialog } from "../hooks/useAlertDialog";

interface PayPalCheckoutButtonProps {
  amount: number; // total amount to charge, e.g. 49.99
  currency: string; // e.g. "USD"
  description: string; // e.g. "Custom Family Tree – 24×36 print"
  customData?: Record<string, any>; // any extra metadata you want to pass to your backend
}

const PayPalCheckoutButton: React.FC<PayPalCheckoutButtonProps> = ({
  amount,
  currency,
  description,
  customData,
}) => {
  const { user } = useAuth();
  const userID = user?.uid ?? null;
  const [{ isPending, isResolved, isRejected }] = usePayPalScriptReducer();
  const { dialogState, showError, showInfo, closeDialog } = useAlertDialog();

  // Store cart data in component state so it's available in both createOrder and onApprove
  const [cartData, setCartData] = useState<any[]>([]);

  // Update cart data when customData changes
  useEffect(() => {
    if (customData?.items) {
      setCartData(customData.items);
      console.log(
        "🛒 PAYPAL BUTTON: Cart data stored in state:",
        customData.items
      );
    }
  }, [customData]);

  // Firebase Functions URLs for the functions (snake_case)
  const CREATE_PAYMENT_URL =
    "https://us-central1-family-tree-and-me.cloudfunctions.net/create_paypal_payment";
  const CONFIRM_PAYMENT_URL =
    "https://us-central1-family-tree-and-me.cloudfunctions.net/confirm_paypal_payment";

  if (!userID) {
    return <div>Please log in to check out.</div>;
  }

  // Show loading state while PayPal script is loading
  if (isPending) {
    return <div>Loading PayPal…</div>;
  }

  // Show error if PayPal script failed to load
  if (isRejected) {
    return (
      <div>Failed to load PayPal. Please refresh the page and try again.</div>
    );
  }

  // Don't render buttons until script is ready
  if (!isResolved) {
    return <div>Initializing PayPal...</div>;
  }

  return (
    <>
      <div style={{ margin: "20px 0" }}>
        <PayPalButtons
          style={{
            layout: "vertical",
            color: "gold",
            shape: "rect",
            label: "paypal",
          }}
          createOrder={async (_, actions) => {
            try {
              console.log("=== PAYPAL CREATE ORDER STARTED ===");
              console.log("Creating PayPal order with:", {
                amount,
                currency,
                description,
                userID,
              });
              console.log("Amount type:", typeof amount);
              console.log("User ID type:", typeof userID);

              // DEBUG: Log the customData being passed
              console.log(
                "🛒 DEBUG CREATE ORDER: customData received:",
                customData
              );
              console.log(
                "🛒 DEBUG CREATE ORDER: customData.items:",
                customData?.items
              );
              console.log(
                "🛒 DEBUG CREATE ORDER: customData.items length:",
                customData?.items?.length || 0
              );

              // Ensure user is authenticated
              if (!userID) {
                throw new Error("User not authenticated");
              }

              const requestPayload = {
                amount: amount.toString(),
                currency,
                description,
                userID,
                customData,
              };
              console.log(
                "Request payload to Cloud Run function:",
                requestPayload
              );

              // Call Cloud Run function to create PayPal order
              console.log("Calling Cloud Run function:", CREATE_PAYMENT_URL);
              const response = await fetch(CREATE_PAYMENT_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(requestPayload),
              });

              console.log("Response status:", response.status);
              console.log("Response headers:", response.headers);

              if (!response.ok) {
                const errorText = await response.text();
                console.error("HTTP error:", response.status, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
              }

              const data = await response.json();
              console.log("Cloud Run function result:", data);

              if (data.orderID) {
                console.log("PayPal order created successfully:", data.orderID);
                return data.orderID;
              } else {
                console.error("No order ID in response:", data);
                console.error("Response keys:", Object.keys(data));
                throw new Error("No order ID received from server");
              }
            } catch (error) {
              console.error("=== PAYPAL CREATE ORDER ERROR ===");
              console.error("Error creating PayPal order:", error);
              console.error("Error type:", typeof error);
              console.error(
                "Error message:",
                error instanceof Error ? error.message : "Unknown error"
              );
              console.error(
                "Error stack:",
                error instanceof Error ? error.stack : "No stack trace"
              );
              throw new Error(
                `Failed to create order: ${
                  error instanceof Error ? error.message : "Unknown error"
                }`
              );
            }
          }}
          onApprove={async (data, actions) => {
            try {
              console.log("=== PAYPAL ON APPROVE STARTED ===");
              console.log("PayPal order approved:", data.orderID);

              // Ensure user is authenticated
              if (!userID) {
                throw new Error("User not authenticated");
              }

              // Use cart data from component state, with fallback to customData
              const cartItems =
                cartData?.length > 0 ? cartData : customData?.items || [];
              console.log("🛒 DEBUG ON APPROVE: cartData state:", cartData);
              console.log(
                "🛒 DEBUG ON APPROVE: cartData length:",
                cartData?.length || 0
              );
              console.log(
                "🛒 DEBUG ON APPROVE: customData.items:",
                customData?.items
              );
              console.log(
                "🛒 DEBUG ON APPROVE: customData.items length:",
                customData?.items?.length || 0
              );
              console.log(
                "🛒 DEBUG ON APPROVE: Final cartItems used:",
                cartItems
              );
              console.log(
                "🛒 DEBUG ON APPROVE: Final cartItems length:",
                cartItems.length
              );
              if (cartItems.length > 0) {
                console.log(
                  "🛒 DEBUG ON APPROVE: First cart item:",
                  cartItems[0]
                );
                console.log(
                  "🛒 DEBUG ON APPROVE: First cart item productId:",
                  cartItems[0]?.productId
                );
                console.log(
                  "🛒 DEBUG ON APPROVE: All cart items:",
                  JSON.stringify(cartItems, null, 2)
                );
              } else {
                console.error("🚨 DEBUG ON APPROVE: NO CART ITEMS FOUND!");
                console.error("🚨 cartData state:", cartData);
                console.error("🚨 customData:", customData);
                console.error("🚨 customData.items:", customData?.items);
              }

              const confirmPayload = {
                orderID: data.orderID,
                userID,
                cartItems,
                customData: { items: cartItems }, // Reconstruct customData with cart items
              };
              console.log(
                "🛒 DEBUG ON APPROVE: Confirm payload:",
                confirmPayload
              );

              // Call Cloud Run function to capture the payment
              const response = await fetch(CONFIRM_PAYMENT_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(confirmPayload),
              });

              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
              }

              const captureData = await response.json();
              console.log("Payment captured:", captureData);

              // Navigate to success page
              window.location.href = `/payment-confirmation?orderID=${data.orderID}`;
            } catch (error) {
              console.error("Error capturing payment:", error);
              throw new Error(
                `Failed to capture payment: ${
                  error instanceof Error ? error.message : "Unknown error"
                }`
              );
            }
          }}
          onError={(err) => {
            console.error("PayPal Buttons error:", err);
            showError(
              (err.message as string) ||
                "Something went wrong with PayPal. Please try again.",
              "PayPal Error"
            );
          }}
          onCancel={() => {
            console.log("User canceled PayPal flow");
            showInfo("Payment was canceled.", "Payment Canceled");
          }}
        />
      </div>

      {/* Alert Dialog */}
      <AlertDialog
        open={dialogState.open}
        onClose={closeDialog}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        showCancel={dialogState.showCancel}
        onConfirm={dialogState.onConfirm}
      />
    </>
  );
};

export default PayPalCheckoutButton;
